import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../config/firebase';
import { doc, onSnapshot, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import './LiveCodeClassroom.css';

const DEFAULT_CODE = '// Start coding here...\nconsole.log("Hello World!");';
const DEFAULT_LANGUAGE = 'javascript';

const LiveCodeClassroom = ({ sessionId, user, isCoach = false }) => {
  const [code, setCode] = useState(DEFAULT_CODE);
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [isLiveSession, setIsLiveSession] = useState(false);
  const [connectedStudents, setConnectedStudents] = useState(0);
  const editorRef = useRef(null);
  const sessionDocRef = doc(db, 'liveCodeSessions', sessionId);

  // Subscribe to live code updates
  useEffect(() => {
    const unsubscribe = onSnapshot(sessionDocRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setCode(data.code || DEFAULT_CODE);
        setLanguage(data.language || DEFAULT_LANGUAGE);
        setIsLiveSession(data.isLive || false);
        setConnectedStudents(data.connectedStudents || 0);
      }
    });

    return () => unsubscribe();
  }, [sessionId, sessionDocRef]);

  // Update code when coach types
  const handleCodeChange = async (newCode) => {
    if (!isCoach || !isLiveSession || !user?.id) return;
    
    setCode(newCode);
    try {
      await setDoc(sessionDocRef, {
        code: newCode,
        lastUpdated: serverTimestamp(),
        updatedBy: user.id
      }, { merge: true });
    } catch (error) {
      console.error('Error updating live code:', error);
    }
  };

  // Toggle live session (coach only)
  const toggleLiveSession = async () => {
    if (!isCoach || !user?.id) return;
    
    const newLiveState = !isLiveSession;
    setIsLiveSession(newLiveState);
    
    try {
      await setDoc(sessionDocRef, {
        isLive: newLiveState,
        sessionStarted: newLiveState ? serverTimestamp() : null,
        sessionEnded: newLiveState ? null : serverTimestamp(),
        coachId: user.id
      }, { merge: true });
    } catch (error) {
      console.error('Error toggling live session:', error);
    }
  };

  // Change language (coach only)
  const changeLanguage = async (newLanguage) => {
    if (!isCoach || !isLiveSession || !user?.id) return;
    
    setLanguage(newLanguage);
    try {
      await setDoc(sessionDocRef, {
        language: newLanguage,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Clear code (coach only)
  const clearCode = async () => {
    if (!isCoach || !isLiveSession) return;
    
    const emptyCode = getEmptyCodeTemplate(language);
    await handleCodeChange(emptyCode);
  };

  useEffect(() => {
    if (!isCoach || !user?.id) return;

    const createDocIfMissing = async () => {
      try {
        const snapshot = await getDoc(sessionDocRef);
        if (!snapshot.exists()) {
          await setDoc(sessionDocRef, {
            code: DEFAULT_CODE,
            language: DEFAULT_LANGUAGE,
            isLive: false,
            connectedStudents: 0,
            createdAt: serverTimestamp(),
            coachId: user.id
          }, { merge: true });
        }
      } catch (error) {
        console.error('Error preparing live session document:', error);
      }
    };

    createDocIfMissing();
  }, [isCoach, sessionId, user?.id, sessionDocRef]);

  const getEmptyCodeTemplate = (lang) => {
    const templates = {
      javascript: '// Start coding here...\nconsole.log("Hello World!");',
      python: '# Start coding here...\nprint("Hello World!")',
      java: '// Start coding here...\npublic class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello World!");\n    }\n}',
      html: '<!DOCTYPE html>\n<html>\n<head>\n    <title>My Page</title>\n</head>\n<body>\n    <h1>Hello World!</h1>\n</body>\n</html>',
      css: '/* Start styling here */\nbody {\n    font-family: Arial, sans-serif;\n}\n\nh1 {\n    color: #333;\n}'
    };
    return templates[lang] || templates.javascript;
  };

  return (
    <div className="live-code-classroom">
      <div className="classroom-header">
        <div className="header-left">
          <h2>🚀 Live Code Classroom</h2>
          <div className="session-status">
            <span className={`status-badge ${isLiveSession ? 'live' : 'offline'}`}>
              {isLiveSession ? '🔴 LIVE' : '⚫ OFFLINE'}
            </span>
            {isLiveSession && (
              <span className="connected-count">
                👥 {connectedStudents} students connected
              </span>
            )}
          </div>
        </div>
        
        <div className="header-controls">
          {isCoach && (
            <button
              onClick={toggleLiveSession}
              className={`toggle-btn ${isLiveSession ? 'stop' : 'start'}`}
            >
              {isLiveSession ? '⏹️ Stop Session' : '▶️ Start Live Session'}
            </button>
          )}
        </div>
      </div>

      <div className="code-editor-container">
        <div className="editor-toolbar">
          <div className="language-selector">
            <label>Language:</label>
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              disabled={!isCoach || !isLiveSession}
              className="language-select"
            >
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="html">HTML</option>
              <option value="css">CSS</option>
            </select>
          </div>
          
          {isCoach && isLiveSession && (
            <div className="editor-actions">
              <button onClick={clearCode} className="clear-btn">
                🗑️ Clear
              </button>
            </div>
          )}
        </div>

        <div className="code-editor">
          <textarea
            ref={editorRef}
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            disabled={!isCoach || !isLiveSession}
            className={`code-textarea ${language} ${!isCoach || !isLiveSession ? 'readonly' : 'editable'}`}
            placeholder={isCoach && !isLiveSession ? 'Start a live session to begin coding...' : 'Watching live code...'}
            spellCheck={false}
          />
          
          {/* Line numbers */}
          <div className="line-numbers">
            {code.split('\n').map((_, index) => (
              <div key={index} className="line-number">
                {index + 1}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="classroom-footer">
        <div className="instructions">
          {isCoach ? (
            isLiveSession ? (
              <p>📝 You are live! Students can see everything you type in real-time.</p>
            ) : (
              <p>🎯 Click "Start Live Session" to begin sharing your code with students.</p>
            )
          ) : (
            isLiveSession ? (
              <p>👀 Watch your coach code in real-time! The session is live.</p>
            ) : (
              <p>⏳ Waiting for the coach to start the live coding session...</p>
            )
          )}
        </div>
        
        {isLiveSession && (
          <div className="sync-indicator">
            <span className="sync-dot"></span>
            <span>Live Sync Active</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveCodeClassroom;
